// =====================================================================
// ConfirmContext — one imperative, promise-based confirm() for the whole
// app, so no page ever falls back to a native window.confirm() (which
// renders the ugly "localhost:4173 says…" browser chrome).
//
// A single <ConfirmModal> is mounted at the app root by <ConfirmProvider>.
// useConfirm() returns an async confirm() that opens that modal and
// resolves true/false on the user's choice — a near drop-in for the old
// `if (!confirm(msg)) return` pattern:
//
//     const confirm = useConfirm()
//     if (!(await confirm('Delete this?'))) return
//     if (!(await confirm({ title: 'Delete table',
//                           message: 'This cannot be undone.',
//                           confirmText: 'Delete',
//                           isDangerous: true }))) return
//
// Accepts either a plain string (message only) or an options object.
// =====================================================================

import React, { createContext, useCallback, useContext, useRef, useState } from 'react'
import ConfirmModal from '../components/ui/ConfirmModal'
import { CLOSED_CONFIRM, normalizeConfirmOptions } from './confirmLogic.mjs'

const ConfirmContext = createContext(null)

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(CLOSED_CONFIRM)
  // Holds the Promise resolver for the currently-open dialog. A ref (not
  // state) so resolving never depends on a re-render landing first.
  const resolverRef = useRef(null)

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      // If a prior dialog is somehow still pending, resolve it false so its
      // caller isn't left hanging forever.
      if (resolverRef.current) resolverRef.current(false)
      resolverRef.current = resolve
      setState(normalizeConfirmOptions(opts))
    })
  }, [])

  const settle = useCallback((result) => {
    const resolve = resolverRef.current
    resolverRef.current = null
    setState((s) => ({ ...s, open: false }))
    if (resolve) resolve(result)
  }, [])

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmModal
        open={state.open}
        title={state.title}
        message={state.message}
        confirmText={state.confirmText}
        isDangerous={state.isDangerous}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    throw new Error('useConfirm() must be used within a <ConfirmProvider>')
  }
  return ctx
}

export default ConfirmContext
